import { useState, useEffect } from 'react';
import { Settings, LogOut, Lock, KeyRound, Loader2 } from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

export const AdminLogin = () => {
  const { isAdmin, isLoading, isConfigured, login, logout, setup, checkSetup } = useAdmin();
  const [showDialog, setShowDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (showDialog) {
      checkSetup();
    }
  }, [showDialog, checkSetup]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const result = await login(password);
    
    if (result.success) {
      setShowDialog(false);
      setPassword('');
    } else {
      setError(result.message || 'كلمة المرور غير صحيحة');
    }
    setIsSubmitting(false);
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError('يجب أن تحتوي كلمة المرور على ١٢ حرفًا على الأقل');
      return;
    }

    if (password !== confirmPassword) {
      setError('كلمتا المرور غير متطابقتين');
      return;
    }

    setIsSubmitting(true);
    const result = await setup(password);
    
    if (result.success) {
      setShowDialog(false);
      setPassword('');
      setConfirmPassword('');
    } else {
      setError(result.message || 'خطأ أثناء الإعداد');
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setShowDialog(false);
    setPassword('');
    setConfirmPassword('');
    setError('');
  };

  if (isLoading) {
    return (
      <Button variant="ghost" size="icon" className="rounded-full" disabled>
        <Loader2 className="h-5 w-5 animate-spin" />
      </Button>
    );
  }

  if (isAdmin) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={logout}
        className="rounded-full hover:bg-muted"
        title="تسجيل الخروج"
        data-testid="admin-logout-btn"
      >
        <LogOut className="h-5 w-5 text-primary" />
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setShowDialog(true)}
        className="rounded-full hover:bg-muted"
        title="وضع الإدارة"
        data-testid="admin-login-btn"
      >
        <Settings className="h-5 w-5" />
      </Button>

      <Dialog open={showDialog} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[380px]" data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isConfigured ? (
                <>
                  <Lock className="h-5 w-5" />
                  وضع الإدارة
                </>
              ) : (
                <>
                  <KeyRound className="h-5 w-5" />
                  الإعداد الأولي
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {isConfigured 
                ? 'أدخل كلمة المرور للوصول إلى الإدارة'
                : 'أنشئ كلمة مرور للمسؤول لتأمين إدارة المحتوى'
              }
            </DialogDescription>
          </DialogHeader>

          {isConfigured ? (
            <form onSubmit={handleLogin} className="space-y-4 pt-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="كلمة المرور"
                className={`rounded-xl ${error ? 'border-destructive' : ''}`}
                data-testid="admin-password-input"
                autoFocus
                disabled={isSubmitting}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button
                type="submit"
                className="w-full rounded-full bg-primary hover:bg-primary/90"
                data-testid="admin-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                    جاري الدخول...
                  </>
                ) : 'تسجيل الدخول'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSetup} className="space-y-4 pt-4">
              <div className="space-y-3">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="كلمة المرور الجديدة"
                  className={`rounded-xl ${error ? 'border-destructive' : ''}`}
                  data-testid="admin-password-input"
                  autoFocus
                  disabled={isSubmitting}
                />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  placeholder="تأكيد كلمة المرور"
                  className={`rounded-xl ${error ? 'border-destructive' : ''}`}
                  data-testid="admin-confirm-password-input"
                  disabled={isSubmitting}
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <p className="text-xs text-muted-foreground">
                ستُستخدم كلمة المرور هذه للوصول إلى وضع الإدارة. احتفظ بها في مكان آمن.
              </p>
              <Button
                type="submit"
                className="w-full rounded-full bg-primary hover:bg-primary/90"
                data-testid="admin-setup-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                    جاري الإعداد...
                  </>
                ) : 'إنشاء كلمة المرور'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
